import { useEffect, useRef, useState, type FormEvent } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { getPublicForm, submitPublicForm } from '@/api/forms'
import FormView, { type FormValues } from '@/features/forms/FormView'

/** True inside an iframe on the agency's own website. */
function isEmbedded(): boolean {
  try {
    return window.self !== window.top
  } catch {
    return true // a cross-origin parent can't even be compared — we're framed
  }
}

/**
 * Opened directly, the form sits on a card in the middle of the page. Embedded in the agency's site it
 * is just the form — no grey page, no shadow, top-aligned — and it tells the page how tall it is, so
 * the iframe can fit it instead of leaving empty space (the embed snippet listens for this).
 */
function Centered({ children, background }: { children: React.ReactNode; background?: string }) {
  const embedded = isEmbedded()
  const ref = useRef<HTMLDivElement>(null)
  const { id } = useParams()
  useEffect(() => {
    if (!embedded || !ref.current) return
    const el = ref.current
    const post = () => window.parent.postMessage({ ppFormId: id, ppFormHeight: Math.ceil(el.getBoundingClientRect().height) }, '*')
    const ro = new ResizeObserver(post)
    ro.observe(el)
    post()
    return () => ro.disconnect()
  }, [embedded, id])
  if (embedded) {
    return (
      <div ref={ref} className="bg-white p-5" style={{ backgroundColor: background }}>
        {children}
      </div>
    )
  }
  return (
    <div className="min-h-screen bg-brand-bg flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md" style={{ backgroundColor: background }}>
        {children}
      </div>
    </div>
  )
}

export default function PublicForm() {
  const { id } = useParams()
  const { data: form, isLoading, isError } = useQuery({
    queryKey: ['public-form', id],
    queryFn: () => getPublicForm(id as string),
    enabled: !!id,
    retry: false,
  })
  const [values, setValues] = useState<FormValues>({})
  const [submitted, setSubmitted] = useState(false)
  const [thankYou, setThankYou] = useState<string | null>(null)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (!id) return
    setSending(true)
    setError(null)
    try {
      const result = await submitPublicForm(id, values)
      if (result?.redirectUrl) {
        // The form is usually embedded in an iframe on the agency's site: navigating `window` would
        // load the destination INSIDE the little frame. Navigate the top window instead. Across origins
        // a frame may only SET top.location.href (location.assign() throws a SecurityError — which used
        // to drop us into the fallback and open the thank-you page inside the frame).
        if (isEmbedded()) {
          try {
            window.top!.location.href = result.redirectUrl
            return
          } catch {
            // the embedding page sandboxed the frame against top navigation — fall through
          }
        }
        window.location.assign(result.redirectUrl)
        return
      }
      setThankYou(result?.thankYouMessage ?? null)
      setSubmitted(true)
    } catch {
      // A lead is the most valuable thing this page captures — never fail silently.
      setError("Sorry, something went wrong sending your details. Please try again.")
    } finally {
      setSending(false)
    }
  }

  if (isLoading) return <Centered><p className="text-gray-400 text-sm">Loading…</p></Centered>
  if (isError || !form) return <Centered><p className="text-gray-500 text-sm">This form is not available.</p></Centered>
  if (submitted)
    return (
      <Centered background={form.style?.background}>
        <h1 className="text-xl font-semibold text-brand-darkest mb-2" style={{ color: form.style?.text }}>Thank you!</h1>
        <p className="text-sm text-gray-500">
          {thankYou || "We've received your details and will be in touch shortly."}
        </p>
      </Centered>
    )

  return (
    <Centered background={form.style?.background}>
      <FormView
        form={form}
        values={values}
        onChange={(key, v) => setValues((vs) => ({ ...vs, [key]: v }))}
        onSubmit={onSubmit}
        sending={sending}
        error={error}
      />
    </Centered>
  )
}
